import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import {
  Report,
  ReportType,
  ReportFilters,
  SalesReportData,
  InventoryReportData,
  ImportOrderReportData,
  LowStockReportData,
  LowStockItem,
  WarehousePerformanceData,
  ProductPerformanceData,
  RevenueReportData,
  RevenueDayItem,
  ReturnsCancellationsReportData,
  ReturnCancelOrderItem,
  ProfitLossReportData,
  ProfitLossProductItem,
  ProfitLossCostItem,
} from './Report';
import { ReservationService } from '../reservation/reservationService';
import {
  CostType,
  CostStatus,
  RecurrenceFrequency,
  COST_CATEGORY_LABELS,
  OCCURRENCES_PER_YEAR,
} from '../import-cost/importCost';

const REPORTS_COLLECTION         = 'reports';
const PRODUCTS_COLLECTION        = 'products';
const IMPORT_ORDERS_COLLECTION   = 'importOrders';
const WAREHOUSES_COLLECTION      = 'warehouses';
const CATEGORIES_COLLECTION      = 'categories';
const COSTS_COLLECTION           = 'costs';

export interface PaginatedReportsResult {
  reports: Report[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface ReportStats {
  total: number;
  completed: number;
  generating: number;
  failed: number;
  byType: { [k: string]: number };
}

export class ReportService {

  private static formatStatus(status: string): string {
    const map: { [k: string]: string } = {
      pending: 'Pending', processing: 'Processing', confirmed: 'Confirmed', shipped: 'Shipped',
      delivered: 'Delivered', cancelled: 'Cancelled', returned: 'Returned', refunded: 'Refunded',
      draft: 'Draft', approved: 'Approved', received: 'Received', paid: 'Paid', partial: 'Partial', unpaid: 'Unpaid',
    };
    return map[status] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  /* ===================== GET REPORTS PAGINATED =====================
   *
   * FIX: Replaced cursor-based pagination (startAfter / endBefore /
   * limitToLast) with a two-step cursor-seek so any page can be fetched
   * in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * Also fixed the broken count query — the original used
   * `queryConstraints.filter(c => c.type === 'where')` which always
   * returned an empty array (Firestore constraint objects have no `.type`
   * property), so getCountFromServer was always returning the unfiltered total.
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
   *             seek). The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   * ================================================================= */
  static async getReportsPaginated(
    pageSize = 10,
    page = 1,
    searchQuery?: string,
    typeFilter?: ReportType,
    statusFilter?: 'completed' | 'generating' | 'failed'
  ): Promise<PaginatedReportsResult> {
    try {
      const reports: Report[] = [];
      const collectionRef = collection(db, REPORTS_COLLECTION);

      // Build where-constraints (shared between count + seek + page queries)
      const whereConstraints: any[] = [];
      if (typeFilter)   whereConstraints.push(where('reportType', '==', typeFilter));
      if (statusFilter) whereConstraints.push(where('status',     '==', statusFilter));

      const orderConstraint = orderBy('generatedAt', 'desc');

      // ── Total count (0 doc reads) — FIX: pass whereConstraints directly ──
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...whereConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...whereConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...whereConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...whereConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ───────────────────────────────────
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const report: Report = {
          id: docSnap.id, reportType: data.reportType, reportName: data.reportName,
          description: data.description || '', filters: this.deserializeFilters(data.filters || {}),
          status: data.status, generatedAt: data.generatedAt?.toDate() || new Date(),
          generatedBy: data.generatedBy, generatedByName: data.generatedByName,
          data: this.deserializeReportData(data.data || {}), summary: data.summary || { totalRecords: 0 }, error: data.error,
        };
        if (searchQuery && searchQuery.trim() !== '') {
          const lower = searchQuery.toLowerCase();
          const matches =
            report.reportName.toLowerCase().includes(lower) ||
            (report.description?.toLowerCase().includes(lower)) ||
            (report.generatedByName?.toLowerCase().includes(lower));
          if (matches) reports.push(report);
        } else {
          reports.push(report);
        }
      });

      return {
        reports,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching paginated reports:', error);
      throw error;
    }
  }

  static async generateReport(
    reportType: ReportType, reportName: string, description: string,
    filters: ReportFilters, userId: string, userName: string
  ): Promise<string> {
    try {
      const reportRef = await addDoc(collection(db, REPORTS_COLLECTION), {
        reportType, reportName, description, filters: this.serializeFilters(filters),
        status: 'generating', generatedAt: serverTimestamp(),
        generatedBy: userId, generatedByName: userName, data: {}, summary: { totalRecords: 0 },
      });
      const reportId = reportRef.id;

      try {
        let data: any, summary: any;
        switch (reportType) {
          case 'sales':                 { const d = await this.generateSalesReport(filters);                data = d; summary = d.summary; break; }
          case 'inventory':             { const d = await this.generateInventoryReport(filters);            data = d; summary = d.summary; break; }
          case 'purchase_orders':       { const d = await this.generateImportOrderReport(filters);        data = d; summary = d.summary; break; }
          case 'low_stock':             { const d = await this.generateLowStockReport(filters);             data = d; summary = d.summary; break; }
          case 'warehouse_performance': { const d = await this.generateWarehousePerformanceReport(filters); data = d; summary = d.summary; break; }
          case 'product_performance':   { const d = await this.generateProductPerformanceReport(filters);   data = d; summary = d.summary; break; }
          case 'revenue':               { const d = await this.generateRevenueReport(filters);              data = d; summary = d.summary; break; }
          case 'returns_cancellations': { const d = await this.generateReturnsCancellationsReport(filters); data = d; summary = d.summary; break; }
          case 'profit_loss':           { const d = await this.generateProfitLossReport(filters);           data = d; summary = d.summary; break; }
          default: throw new Error('Invalid report type');
        }

        await updateDoc(reportRef, {
          status: 'completed', data: this.serializeReportData(data),
          summary: {
            ...summary,
            totalRecords:
              data.purchaseOrders?.length || data.orders?.length || data.products?.length ||
              data.items?.length || data.warehouses?.length || data.dailyRevenue?.length ||
              data.revenueByProduct?.length || 0,
          },
        });
        return reportId;
      } catch (error) {
        await updateDoc(reportRef, { status: 'failed', error: error instanceof Error ? error.message : 'Unknown error' });
        throw error;
      }
    } catch (error) {
      console.error('Error generating report:', error);
      throw error;
    }
  }

  private static serializeFilters(filters: ReportFilters): any {
    return {
      dateFrom: filters.dateFrom ? Timestamp.fromDate(filters.dateFrom) : null,
      dateTo:   filters.dateTo   ? Timestamp.fromDate(filters.dateTo)   : null,
      warehouseId: filters.warehouseId || null, categoryId: filters.categoryId || null,
      subcategoryId: filters.subcategoryId || null, productId: filters.productId || null,
      status: filters.status || null, supplierId: filters.supplierId || null,
    };
  }

  private static deserializeFilters(filters: any): ReportFilters {
    return {
      dateFrom: filters.dateFrom?.toDate(), dateTo: filters.dateTo?.toDate(),
      warehouseId: filters.warehouseId, categoryId: filters.categoryId,
      subcategoryId: filters.subcategoryId, productId: filters.productId,
      status: filters.status, supplierId: filters.supplierId,
    };
  }

  private static serializeReportData(data: any): any {
    if (!data) return data;
    if (Array.isArray(data)) return data.map(i => this.serializeReportData(i));
    if (typeof data === 'object' && data !== null) {
      if (data instanceof Date) return Timestamp.fromDate(data);
      const out: any = {};
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          const v = this.serializeReportData(data[key]);
          if (v !== undefined) out[key] = v;
        }
      }
      return out;
    }
    return data;
  }

  private static deserializeReportData(data: any): any {
    if (!data) return data;
    if (Array.isArray(data)) return data.map(i => this.deserializeReportData(i));
    if (typeof data === 'object' && data !== null) {
      if (data.toDate && typeof data.toDate === 'function') return data.toDate();
      const out: any = {};
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) out[key] = this.deserializeReportData(data[key]);
      }
      return out;
    }
    return data;
  }

  private static async getCategoryName(categoryId: string): Promise<string> {
    try {
      const snap = await getDoc(doc(db, CATEGORIES_COLLECTION, categoryId));
      return snap.exists() ? snap.data().name || 'Unknown' : 'Unknown';
    } catch { return 'Unknown'; }
  }

  // Every sold car currently on Firestore — the real source of truth for
  // Sales / Revenue / Product Performance / Profit & Loss below, replacing
  // the old e-commerce `orders` collection that nothing writes to anymore
  // (a car sale is recorded on the Product doc itself, see
  // admin/src/admin/pages/sold-vehicles/soldVehicleService.ts).
  private static async getSoldCars(filters: ReportFilters): Promise<any[]> {
    const snapshot = await getDocs(query(collection(db, PRODUCTS_COLLECTION), where('status', '==', 'Sold')));
    let cars = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as any));
    if (filters.dateFrom) {
      const from = filters.dateFrom.getTime();
      cars = cars.filter(c => (c.soldAt?.toDate?.() || c.soldAt)?.getTime?.() >= from);
    }
    if (filters.dateTo) {
      const to = filters.dateTo.getTime();
      cars = cars.filter(c => (c.soldAt?.toDate?.() || c.soldAt)?.getTime?.() <= to);
    }
    if (filters.categoryId) cars = cars.filter(c => c.categoryId === filters.categoryId);
    if (filters.warehouseId) cars = cars.filter(c => c.branchId === filters.warehouseId);
    return cars;
  }

  private static async generateSalesReport(filters: ReportFilters): Promise<SalesReportData> {
    const soldCars = await this.getSoldCars(filters);
    soldCars.sort((a, b) => {
      const at = (a.soldAt?.toDate?.() || a.soldAt || new Date(0)).getTime();
      const bt = (b.soldAt?.toDate?.() || b.soldAt || new Date(0)).getTime();
      return bt - at;
    });

    const orders = soldCars.map(car => {
      const finalPrice = car.finalSalePrice || 0;
      const deposit = car.depositAmount || 0;
      const tradeIn = car.tradeInValue || 0;
      const balanceDue = Math.max(0, finalPrice - deposit - tradeIn);
      return {
        orderId: car.id,
        orderNumber: car.sku || 'N/A',
        orderDate: car.soldAt?.toDate?.() || car.soldAt || new Date(),
        customerName: car.buyerName || 'Unknown',
        customerEmail: car.buyerEmail || 'N/A',
        items: 1,
        subtotal: finalPrice,
        taxAmount: 0,
        shippingCost: 0,
        discountAmount: tradeIn,
        totalAmount: finalPrice,
        paymentStatus: balanceDue > 0 ? 'Partial' : 'Paid',
        paymentMethod: car.paymentMethod || 'N/A',
        status: 'Sold',
      };
    });

    const totalRevenue = orders.reduce((s, o) => s + o.totalAmount, 0);

    const productMap = new Map<string, { quantity: number; revenue: number }>();
    for (const o of orders) {
      const car = soldCars.find(c => c.id === o.orderId);
      const name = car?.name || 'Unknown Vehicle';
      const ex = productMap.get(name) || { quantity: 0, revenue: 0 };
      productMap.set(name, { quantity: ex.quantity + 1, revenue: ex.revenue + o.totalAmount });
    }
    const topProducts = Array.from(productMap.entries())
      .map(([productName, d]) => ({ productName, ...d }))
      .sort((a, b) => b.revenue - a.revenue).slice(0, 10);

    const statusBreakdown: { [k: string]: number } = { Sold: orders.length };

    const paymentMethodBreakdown: { [k: string]: number } = {};
    orders.forEach(o => { paymentMethodBreakdown[o.paymentMethod] = (paymentMethodBreakdown[o.paymentMethod] || 0) + 1; });

    return {
      orders,
      summary: {
        totalOrders: orders.length, totalRevenue, totalTax: 0, totalShipping: 0,
        totalDiscount: orders.reduce((s, o) => s + o.discountAmount, 0),
        averageOrderValue: orders.length > 0 ? totalRevenue / orders.length : 0,
        topProducts, statusBreakdown, paymentMethodBreakdown,
      },
    };
  }

  // A car has exactly one physical unit at one branch — "stock" here means
  // "is this specific VIN currently available to sell", not a restockable
  // quantity. status buckets: In Stock → in_stock, Reserved → low_stock
  // (spoken for, about to be unavailable), Sold/Delivered/In Transit → out_of_stock.
  private static stockBucket(status: string): 'in_stock' | 'low_stock' | 'out_of_stock' {
    if (status === 'In Stock') return 'in_stock';
    if (status === 'Reserved') return 'low_stock';
    return 'out_of_stock';
  }

  private static async generateInventoryReport(filters: ReportFilters): Promise<InventoryReportData> {
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    const constraints: any[] = [];
    if (filters.categoryId)    constraints.push(where('categoryId',    '==', filters.categoryId));
    if (filters.subcategoryId) constraints.push(where('subcategoryId', '==', filters.subcategoryId));
    const snapshot = await getDocs(constraints.length > 0 ? query(productsRef, ...constraints) : query(productsRef));

    const categoryNameCache = new Map<string, string>();
    const products: any[] = [];
    let totalStock = 0, totalValue = 0;

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      if (filters.warehouseId && data.branchId !== filters.warehouseId) continue;

      if (data.categoryId && !categoryNameCache.has(data.categoryId)) {
        categoryNameCache.set(data.categoryId, await this.getCategoryName(data.categoryId));
      }
      const categoryName = data.categoryId ? categoryNameCache.get(data.categoryId) || 'Uncategorized' : 'Uncategorized';

      const bucket = this.stockBucket(data.status || 'In Stock');
      const unitStock = bucket === 'in_stock' ? 1 : 0;
      const unitValue = unitStock * (data.price || 0);

      products.push({
        productId: docSnap.id,
        productName: data.name || 'Unknown',
        sku: data.sku || 'N/A',
        category: categoryName,
        subcategory: '',
        hasVariants: false,
        totalStock: unitStock,
        stockValue: unitValue,
        warehouses: [{
          warehouseId: data.branchId || 'unassigned',
          warehouseName: data.branchName || 'Unassigned',
          quantity: unitStock,
          status: bucket,
        }],
        status: bucket,
      });
      totalStock += unitStock;
      totalValue += unitValue;
    }

    return {
      products,
      summary: {
        totalProducts: products.length, totalStock, totalValue,
        outOfStockItems: products.filter(p => p.status === 'out_of_stock').length,
        inStockItems: products.filter(p => p.status === 'in_stock').length,
      },
    };
  }

  // Import Orders Report — the real e-commerce-Purchase-Order-shaped module
  // this once was (see admin/src/admin/pages/import-orders), left as-is here
  // since it's already correctly modeling a real dealership workflow
  // (ordering vehicle stock from suppliers), just under a car-specific label.
  private static async generateImportOrderReport(filters: ReportFilters): Promise<ImportOrderReportData> {
    const poRef = collection(db, IMPORT_ORDERS_COLLECTION);
    let constraints: any[] = [];
    if (filters.dateFrom)   constraints.push(where('createdAt',   '>=', Timestamp.fromDate(filters.dateFrom)));
    if (filters.dateTo)     constraints.push(where('createdAt',   '<=', Timestamp.fromDate(filters.dateTo)));
    if (filters.status)     constraints.push(where('status',       '==', filters.status));
    if (filters.supplierId) constraints.push(where('supplierId',   '==', filters.supplierId));
    constraints.push(orderBy('createdAt', 'desc'));

    const snapshot = await getDocs(query(poRef, ...constraints));
    const purchaseOrders = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      return {
        poId: docSnap.id, poNumber: data.poNumber || 'N/A',
        supplierName: data.supplierName || 'Unknown Supplier', supplierEmail: data.supplierEmail || 'N/A', supplierPhone: data.supplierPhone || 'N/A',
        orderDate: data.createdAt?.toDate() || new Date(), expectedDelivery: data.expectedDeliveryDate?.toDate() || null,
        actualDelivery: data.actualDeliveryDate?.toDate() || data.receivedAt?.toDate() || null,
        approvedAt: data.approvedAt?.toDate() || null, receivedAt: data.receivedAt?.toDate() || null,
        items: data.items?.length || 0, subtotal: data.subtotal || 0, taxAmount: data.taxAmount || 0,
        shippingCost: data.shippingCost || 0, totalAmount: data.totalAmount || 0, paidAmount: data.paidAmount || 0,
        status: this.formatStatus(data.status || 'draft'), paymentStatus: this.formatStatus(data.paymentStatus || 'pending'),
        hasReturns: data.hasReturns || false,
      };
    });

    const totalAmount = purchaseOrders.reduce((s, p) => s + p.totalAmount, 0);
    const totalPaid   = purchaseOrders.reduce((s, p) => s + p.paidAmount,  0);
    const statusBreakdown: { [k: string]: number } = {};
    purchaseOrders.forEach(p => { statusBreakdown[p.status] = (statusBreakdown[p.status] || 0) + 1; });
    const paymentStatusBreakdown: { [k: string]: number } = {};
    purchaseOrders.forEach(p => { paymentStatusBreakdown[p.paymentStatus] = (paymentStatusBreakdown[p.paymentStatus] || 0) + 1; });
    const supplierMap = new Map<string, { totalAmount: number; poCount: number }>();
    purchaseOrders.forEach(p => { const ex = supplierMap.get(p.supplierName) || { totalAmount: 0, poCount: 0 }; supplierMap.set(p.supplierName, { totalAmount: ex.totalAmount + p.totalAmount, poCount: ex.poCount + 1 }); });

    return {
      purchaseOrders,
      summary: { totalPOs: purchaseOrders.length, totalAmount, totalPaid, totalPending: totalAmount - totalPaid, statusBreakdown, paymentStatusBreakdown, topSuppliers: Array.from(supplierMap.entries()).map(([supplierName, d]) => ({ supplierName, ...d })).sort((a, b) => b.totalAmount - a.totalAmount).slice(0, 10) },
    };
  }

  // "Low Stock Alert" doesn't map onto restockable quantities for cars — a
  // dealership's real equivalent is "vehicles that are about to become
  // unavailable": Reserved (spoken for, pending sale) and In Transit
  // (ordered but not yet on the lot to show a walk-in customer).
  private static async generateLowStockReport(filters: ReportFilters): Promise<LowStockReportData> {
    const snapshot = await getDocs(collection(db, PRODUCTS_COLLECTION));
    const items: LowStockItem[] = [];

    for (const productDoc of snapshot.docs) {
      const data = productDoc.data();
      if (filters.warehouseId && data.branchId !== filters.warehouseId) continue;
      const status: string = data.status || 'In Stock';
      if (status !== 'Reserved' && status !== 'In Transit (Import)') continue;

      items.push({
        productId: productDoc.id,
        productName: data.name || 'Unknown Product',
        sku: data.sku || 'N/A',
        warehouseId: data.branchId || 'unassigned',
        warehouseName: data.branchName || 'Unassigned',
        currentStock: status === 'In Transit (Import)' ? 0 : 1,
        minStock: 1,
        maxStock: 1,
        deficit: status === 'In Transit (Import)' ? 1 : 0,
        status: status === 'In Transit (Import)' ? 'out_of_stock' : 'low_stock',
      });
    }

    return {
      items,
      summary: {
        totalLowStockItems: items.filter(i => i.status === 'low_stock').length,
        totalOutOfStockItems: items.filter(i => i.status === 'out_of_stock').length,
        affectedWarehouses: new Set(items.map(i => i.warehouseId)).size,
        affectedProducts: new Set(items.map(i => i.productId)).size,
      },
    };
  }

  private static async generateWarehousePerformanceReport(filters: ReportFilters): Promise<WarehousePerformanceData> {
    const [warehouseSnapshot, productsSnapshot] = await Promise.all([
      getDocs(collection(db, WAREHOUSES_COLLECTION)),
      getDocs(collection(db, PRODUCTS_COLLECTION)),
    ]);
    const allCars = productsSnapshot.docs.map(d => ({ id: d.id, ...d.data() } as any));

    const warehouses = warehouseSnapshot.docs.map(docSnap => {
      const data = docSnap.data();
      const branchCars = allCars.filter(c => c.branchId === docSnap.id);
      const inStock = branchCars.filter(c => this.stockBucket(c.status || 'In Stock') === 'in_stock');
      const lowStock = branchCars.filter(c => this.stockBucket(c.status || 'In Stock') === 'low_stock');
      const outOfStock = branchCars.filter(c => this.stockBucket(c.status || 'In Stock') === 'out_of_stock');
      const stockValue = inStock.reduce((s, c) => s + (c.price || 0), 0);

      // capacity.used on the warehouse doc is a manually-typed number nothing
      // keeps in sync with real inventory — compute occupied capacity from
      // actual cars at this branch instead (In Stock/Reserved/Sold = physically present).
      const occupyingStatuses = new Set(['In Stock', 'Reserved', 'Sold']);
      const capacityUsed  = branchCars.filter(c => occupyingStatuses.has(c.status)).length;
      const capacityTotal = data.capacity?.total || 1;
      return {
        warehouseId: docSnap.id, warehouseName: data.name || 'Unknown', code: data.code || 'N/A',
        address: `${data.address?.street || ''}, ${data.address?.city || ''}, ${data.address?.state || ''}`.trim(),
        manager: data.contact?.manager || 'N/A',
        totalProducts: branchCars.length,
        totalStock: inStock.length,
        stockValue,
        capacityUsed, capacityTotal, capacityAvailable: data.capacity?.available || 0,
        utilizationRate: Math.round((capacityUsed / capacityTotal) * 100),
        lowStockCount: lowStock.length,
        outOfStockCount: outOfStock.length,
      };
    });

    const totalCapacity  = warehouses.reduce((s, w) => s + w.capacityTotal,    0);
    const totalUsed      = warehouses.reduce((s, w) => s + w.capacityUsed,     0);
    const totalAvailable = warehouses.reduce((s, w) => s + w.capacityAvailable,0);
    const avgUtil        = warehouses.length > 0 ? warehouses.reduce((s, w) => s + w.utilizationRate, 0) / warehouses.length : 0;
    const mostUtilized   = warehouses.reduce((mx, w) => w.utilizationRate > mx.utilizationRate ? w : mx, warehouses[0] || { warehouseName: 'N/A', utilizationRate: 0 });
    const leastUtilized  = warehouses.reduce((mn, w) => w.utilizationRate < mn.utilizationRate ? w : mn, warehouses[0] || { warehouseName: 'N/A', utilizationRate: 0 });

    return { warehouses, summary: { totalWarehouses: warehouses.length, totalCapacity, totalUsed, totalAvailable, averageUtilization: Math.round(avgUtil), mostUtilizedWarehouse: mostUtilized.warehouseName, leastUtilizedWarehouse: leastUtilized.warehouseName } };
  }

  // "Product Performance" for a dealership means "which model/spec sells
  // best" — grouped by the car's exact listing name (the same grouping key
  // client/src/pages/Services/productService.ts groupProductsForListing()
  // uses for identical brand-new units), not per-VIN.
  private static async generateProductPerformanceReport(filters: ReportFilters): Promise<ProductPerformanceData> {
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    const pC: any[] = [];
    if (filters.categoryId)    pC.push(where('categoryId',    '==', filters.categoryId));
    if (filters.subcategoryId) pC.push(where('subcategoryId', '==', filters.subcategoryId));
    const productsSnapshot = await getDocs(pC.length > 0 ? query(productsRef, ...pC) : query(productsRef));
    const allCars = productsSnapshot.docs.map(d => ({ id: d.id, ...d.data() } as any));

    const soldCars = await this.getSoldCars(filters);

    const categoryNameCache = new Map<string, string>();
    const groups = new Map<string, any[]>();
    for (const car of allCars) {
      const key = car.name || 'Unknown Vehicle';
      const list = groups.get(key) || [];
      list.push(car);
      groups.set(key, list);
    }

    const products: any[] = [];
    for (const [name, cars] of Array.from(groups.entries())) {
      const sample = cars[0];
      if (sample.categoryId && !categoryNameCache.has(sample.categoryId)) {
        categoryNameCache.set(sample.categoryId, await this.getCategoryName(sample.categoryId));
      }
      const categoryName = sample.categoryId ? categoryNameCache.get(sample.categoryId) || 'Uncategorized' : 'Uncategorized';

      const soldInGroup = soldCars.filter(c => (c.name || 'Unknown Vehicle') === name);
      const totalSold = soldInGroup.length;
      const revenue = soldInGroup.reduce((s, c) => s + (c.finalSalePrice || 0), 0);
      const currentStock = cars.filter(c => this.stockBucket(c.status || 'In Stock') === 'in_stock').length;
      const averagePrice = totalSold > 0 ? revenue / totalSold : (sample.price || 0);
      const avgDiscount = cars.reduce((s, c) => s + (c.discount || 0), 0) / cars.length;

      products.push({
        productId: sample.id, productName: name, sku: cars.length === 1 ? sample.sku : `${cars.length} units`,
        category: categoryName, subcategory: '', hasVariants: false,
        totalSold, revenue, averagePrice, currentStock,
        stockTurnover: currentStock > 0 ? Math.round((totalSold / currentStock) * 100) / 100 : 0,
        isFeatured: cars.some(c => c.isFeatured), discount: Math.round(avgDiscount * 100) / 100,
      });
    }

    const totalRevenue   = products.reduce((s, p) => s + p.revenue,       0);
    const totalUnitsSold = products.reduce((s, p) => s + p.totalSold,      0);
    const avgTurnover    = products.length > 0 ? products.reduce((s, p) => s + p.stockTurnover, 0) / products.length : 0;
    const avgDiscount    = products.length > 0 ? products.reduce((s, p) => s + p.discount,      0) / products.length : 0;
    const bestSelling    = products.reduce((mx, p) => p.totalSold > mx.totalSold ? p : mx, products[0] || { productName: 'N/A', totalSold: 0 });
    const worstSelling   = products.reduce((mn, p) => (p.totalSold < mn.totalSold && p.totalSold > 0) ? p : mn, products.find(p => p.totalSold > 0) || { productName: 'N/A', totalSold: 0 });

    return { products, summary: { totalProducts: products.length, totalRevenue, totalUnitsSold, bestSellingProduct: bestSelling.productName, worstSellingProduct: worstSelling.productName, averageStockTurnover: Math.round(avgTurnover * 100) / 100, averageDiscount: Math.round(avgDiscount * 100) / 100 } };
  }

  private static async generateRevenueReport(filters: ReportFilters): Promise<RevenueReportData> {
    const soldCars = await this.getSoldCars(filters);

    const dailyRevenueMap = new Map<string, { totalOrders: number; totalRevenue: number }>();
    const productRevenueMap = new Map<string, { productId: string; productName: string; totalSold: number; revenue: number }>();
    const categoryRevenueMap = new Map<string, { categoryId: string; revenue: number }>();

    for (const car of soldCars) {
      const soldAt: Date = car.soldAt?.toDate?.() || car.soldAt || new Date();
      const dateKey = soldAt.toISOString().split('T')[0];
      const ex = dailyRevenueMap.get(dateKey) || { totalOrders: 0, totalRevenue: 0 };
      dailyRevenueMap.set(dateKey, { totalOrders: ex.totalOrders + 1, totalRevenue: ex.totalRevenue + (car.finalSalePrice || 0) });

      const name = car.name || 'Unknown Vehicle';
      const exP = productRevenueMap.get(name) || { productId: car.id, productName: name, totalSold: 0, revenue: 0 };
      productRevenueMap.set(name, { ...exP, totalSold: exP.totalSold + 1, revenue: exP.revenue + (car.finalSalePrice || 0) });

      if (car.categoryId) {
        const exC = categoryRevenueMap.get(car.categoryId) || { categoryId: car.categoryId, revenue: 0 };
        categoryRevenueMap.set(car.categoryId, { categoryId: car.categoryId, revenue: exC.revenue + (car.finalSalePrice || 0) });
      }
    }

    const dailyRevenue: RevenueDayItem[] = Array.from(dailyRevenueMap.entries())
      .map(([dateStr, d]) => ({ date: new Date(dateStr), totalOrders: d.totalOrders, totalRevenue: d.totalRevenue, totalTax: 0, totalShipping: 0, totalDiscount: 0, netRevenue: d.totalRevenue, averageOrderValue: d.totalOrders > 0 ? d.totalRevenue / d.totalOrders : 0 }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    const totalRevSum   = dailyRevenue.reduce((s, d) => s + d.totalRevenue, 0);
    const topProducts   = Array.from(productRevenueMap.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 10).map(p => ({ ...p, percentageOfTotal: totalRevSum > 0 ? (p.revenue / totalRevSum) * 100 : 0 }));
    const topCategories = await Promise.all(Array.from(categoryRevenueMap.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 10).map(async c => ({ categoryId: c.categoryId, categoryName: await this.getCategoryName(c.categoryId), revenue: c.revenue, percentageOfTotal: totalRevSum > 0 ? (c.revenue / totalRevSum) * 100 : 0 })));

    const totalOrders     = dailyRevenue.reduce((s, d) => s + d.totalOrders,   0);
    const highestDay      = dailyRevenue.reduce((mx, d) => d.totalRevenue > mx.totalRevenue ? d : mx, dailyRevenue[0] || { totalRevenue: 0, date: new Date() });
    const lowestDay       = dailyRevenue.reduce((mn, d) => d.totalRevenue < mn.totalRevenue ? d : mn, dailyRevenue[0] || { totalRevenue: 0, date: new Date() });

    let growthRate = 0;
    if (dailyRevenue.length >= 2) {
      const half = Math.floor(dailyRevenue.length / 2);
      const firstHalfRev  = dailyRevenue.slice(0, half).reduce((s, d) => s + d.totalRevenue, 0);
      const secondHalfRev = dailyRevenue.slice(half).reduce((s, d) => s + d.totalRevenue, 0);
      if (firstHalfRev > 0) growthRate = ((secondHalfRev - firstHalfRev) / firstHalfRev) * 100;
    }

    return { dailyRevenue, topProducts, topCategories, summary: { totalRevenue: totalRevSum, totalNetRevenue: totalRevSum, totalOrders, totalTax: 0, totalShipping: 0, totalDiscount: 0, averageOrderValue: totalOrders > 0 ? totalRevSum / totalOrders : 0, averageDailyRevenue: dailyRevenue.length > 0 ? totalRevSum / dailyRevenue.length : 0, highestRevenueDay: highestDay.date.toLocaleDateString(), lowestRevenueDay: lowestDay.date.toLocaleDateString(), growthRate: Math.round(growthRate * 100) / 100 } };
  }

  // Cars aren't "returned" like retail SKUs — the equivalent lost-deal event
  // for a dealership is a Reservation that fell through (Cancelled) or went
  // cold (Lost). Reuses the same interface shape (order-shaped fields) so
  // the existing report viewer/PDF export need no changes.
  private static async generateReturnsCancellationsReport(filters: ReportFilters): Promise<ReturnsCancellationsReportData> {
    const allReservations = await ReservationService.getAll();
    let reservations = allReservations;
    if (filters.dateFrom) {
      const from = filters.dateFrom.getTime();
      reservations = reservations.filter(r => (r.updatedAt as any)?.toDate?.()?.getTime?.() >= from || (r.updatedAt as any)?.getTime?.() >= from);
    }
    if (filters.dateTo) {
      const to = filters.dateTo.getTime();
      reservations = reservations.filter(r => ((r.updatedAt as any)?.toDate?.()?.getTime?.() ?? Infinity) <= to || ((r.updatedAt as any)?.getTime?.() ?? Infinity) <= to);
    }

    const lostDeals = reservations.filter(r => r.status === 'Cancelled' || r.status === 'Lost');

    const orders: ReturnCancelOrderItem[] = lostDeals.map(r => ({
      orderId: r.id || '',
      orderNumber: r.carName || r.id?.slice(0, 8) || 'N/A',
      orderDate: (r.updatedAt as any)?.toDate?.() || (r.updatedAt as any) || new Date(),
      customerName: r.customerName || 'Unknown',
      customerEmail: r.customerEmail || 'N/A',
      totalAmount: r.carPrice || 0,
      status: r.status === 'Cancelled' ? 'Cancelled' : 'Refunded',
      paymentMethod: r.paymentMethod || 'N/A',
      paymentStatus: 'N/A',
      items: 1,
      reason: r.cancellationReason || (r.status === 'Lost' ? 'Inquiry went cold' : ''),
    }));

    const productMap = new Map<string, { quantity: number; revenue: number }>();
    lostDeals.forEach(r => {
      const name = r.carName || 'Unknown Vehicle';
      const ex = productMap.get(name) || { quantity: 0, revenue: 0 };
      productMap.set(name, { quantity: ex.quantity + 1, revenue: ex.revenue + (r.carPrice || 0) });
    });

    const totalLostRevenue = orders.reduce((s, o) => s + o.totalAmount, 0);
    const totalCancelled   = orders.filter(o => o.status === 'Cancelled').length;
    const totalRefunded    = orders.filter(o => o.status === 'Refunded').length; // "Lost" inquiries
    const totalAllReservations = reservations.length;
    const cancellationRate = totalAllReservations > 0 ? ((totalCancelled + totalRefunded) / totalAllReservations) * 100 : 0;

    return {
      orders,
      summary: {
        totalReturned: 0, totalCancelled, totalRefunded, totalOrders: orders.length, totalLostRevenue,
        returnRate: 0, cancellationRate: Math.round(cancellationRate * 100) / 100,
        topReturnedProducts: Array.from(productMap.entries()).map(([productName, d]) => ({ productName, ...d })).sort((a, b) => b.revenue - a.revenue).slice(0, 10),
      },
    };
  }

  // ── Profit & Loss helpers ────────────────────────────────────────────────
  // Average calendar days in one occurrence of a recurring cost's frequency,
  // derived from OCCURRENCES_PER_YEAR (cost.ts) so the two never drift apart.
  private static daysInPeriod(frequency: RecurrenceFrequency): number {
    return 365 / OCCURRENCES_PER_YEAR[frequency];
  }

  private static daysBetweenInclusive(start: Date, end: Date): number {
    return Math.floor((end.getTime() - start.getTime()) / 86_400_000) + 1;
  }

  /**
   * Profit & Loss for [filters.dateFrom, filters.dateTo] (required — a
   * recurring cost can only be prorated against a bounded range).
   *
   * revenue / COGS come from cars sold within the range:
   *   revenue = sum(finalSalePrice)
   *   cogs    = sum(unitCost)   ("landed/purchase cost" on the Car record —
   *             set when the car was added/imported, see enums/product.ts)
   *
   * Operating costs come from the Costs feature (unchanged — already a real,
   * car-dealership-appropriate module, not e-commerce-shaped):
   *   - one-time costs count in full if incurredDate falls in the range.
   *   - recurring costs are prorated: dailyRate = amount / daysInPeriod(freq),
   *     multiplied by the number of days the cost's active window
   *     (incurredDate..endDate, capped to the report range) overlaps the
   *     report range. Paused recurring costs are excluded — there's no
   *     recorded "paused since" date, so we can't know which days within
   *     the range they were actually active.
   */
  private static async generateProfitLossReport(filters: ReportFilters): Promise<ProfitLossReportData> {
    if (!filters.dateFrom || !filters.dateTo) {
      throw new Error('Profit & Loss report requires both a start and end date.');
    }
    const rangeStart = filters.dateFrom;
    const rangeEnd = filters.dateTo;

    const soldCars = await this.getSoldCars(filters);

    const productMap = new Map<string, ProfitLossProductItem>();
    let revenue = 0;
    let cogs = 0;

    for (const car of soldCars) {
      const itemRevenue = car.finalSalePrice || 0;
      const itemCogs = car.unitCost || 0;
      revenue += itemRevenue;
      cogs += itemCogs;

      productMap.set(car.id, {
        productId: car.id,
        productName: car.name || 'Unknown Vehicle',
        quantitySold: 1, revenue: itemRevenue, cogs: itemCogs, grossProfit: itemRevenue - itemCogs,
      });
    }

    const revenueByProduct = Array.from(productMap.values()).sort((a, b) => b.grossProfit - a.grossProfit);
    const grossProfit = revenue - cogs;

    // ── Operating costs from the Costs feature, prorated into the range ────
    const costsSnapshot = await getDocs(collection(db, COSTS_COLLECTION));
    const costBreakdownMap = new Map<string, ProfitLossCostItem>();
    let operatingCostsOneTime = 0;
    let operatingCostsRecurring = 0;

    costsSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      const category: string = data.category || 'other';
      const amount: number = data.amount || 0;
      const incurredDate: Date | undefined = data.incurredDate?.toDate();
      if (!incurredDate) return;

      let contributed = 0;

      if (data.costType === CostType.ONE_TIME) {
        if (incurredDate >= rangeStart && incurredDate <= rangeEnd) {
          contributed = amount;
          operatingCostsOneTime += contributed;
        }
      } else if (data.costType === CostType.RECURRING) {
        if (data.status === CostStatus.PAUSED) return;
        const frequency: RecurrenceFrequency | undefined = data.recurrenceFrequency;
        if (!frequency) return;

        const costEnd: Date | undefined = data.endDate?.toDate();
        const overlapStart = incurredDate > rangeStart ? incurredDate : rangeStart;
        const overlapEnd = costEnd && costEnd < rangeEnd ? costEnd : rangeEnd;

        if (overlapEnd >= overlapStart) {
          const overlapDays = this.daysBetweenInclusive(overlapStart, overlapEnd);
          const dailyRate = amount / this.daysInPeriod(frequency);
          contributed = dailyRate * overlapDays;
          operatingCostsRecurring += contributed;
        }
      }

      if (contributed > 0) {
        const existing = costBreakdownMap.get(category) || {
          category: COST_CATEGORY_LABELS[category as keyof typeof COST_CATEGORY_LABELS] || category,
          amount: 0, oneTimeAmount: 0, recurringAmount: 0, count: 0,
        };
        existing.amount += contributed;
        if (data.costType === CostType.ONE_TIME) existing.oneTimeAmount += contributed;
        else existing.recurringAmount += contributed;
        existing.count += 1;
        costBreakdownMap.set(category, existing);
      }
    });

    const costBreakdown = Array.from(costBreakdownMap.values()).sort((a, b) => b.amount - a.amount);
    const operatingCosts = operatingCostsOneTime + operatingCostsRecurring;
    const netProfit = grossProfit - operatingCosts;
    const periodDays = this.daysBetweenInclusive(rangeStart, rangeEnd);

    return {
      revenueByProduct,
      costBreakdown,
      summary: {
        totalRecords: revenueByProduct.length,
        periodDays,
        totalOrders: soldCars.length,
        revenue,
        cogs,
        grossProfit,
        grossMarginPercent: revenue > 0 ? (grossProfit / revenue) * 100 : 0,
        operatingCosts,
        operatingCostsOneTime,
        operatingCostsRecurring,
        netProfit,
        netMarginPercent: revenue > 0 ? (netProfit / revenue) * 100 : 0,
      },
    };
  }

  /** @deprecated Use getReportsPaginated */
  static async getReports(): Promise<Report[]> {
    try {
      const q = query(collection(db, REPORTS_COLLECTION), orderBy('generatedAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return { id: doc.id, ...data, generatedAt: data.generatedAt?.toDate(), filters: this.deserializeFilters(data.filters || {}), data: this.deserializeReportData(data.data || {}) } as Report;
      });
    } catch (error) { console.error('Error fetching reports:', error); throw error; }
  }

  static async getReport(reportId: string): Promise<Report | null> {
    try {
      const snap = await getDoc(doc(db, REPORTS_COLLECTION, reportId));
      if (!snap.exists()) return null;
      const data = snap.data();
      return { id: snap.id, ...data, generatedAt: data.generatedAt?.toDate(), filters: this.deserializeFilters(data.filters || {}), data: this.deserializeReportData(data.data || {}) } as Report;
    } catch (error) { console.error('Error fetching report:', error); throw error; }
  }

  static async deleteReport(reportId: string): Promise<{ success: boolean; message: string }> {
    try {
      await deleteDoc(doc(db, REPORTS_COLLECTION, reportId));
      return { success: true, message: 'Report deleted successfully.' };
    } catch (error: any) {
      console.error('Error deleting report:', error);
      return { success: false, message: error.message || 'Failed to delete report.' };
    }
  }

  static async getReportStats(): Promise<ReportStats> {
    try {
      const col = collection(db, REPORTS_COLLECTION);

      const [totalSnap, completedSnap, generatingSnap, failedSnap, allDocsSnap] = await Promise.all([
        getCountFromServer(query(col)),
        getCountFromServer(query(col, where('status', '==', 'completed'))),
        getCountFromServer(query(col, where('status', '==', 'generating'))),
        getCountFromServer(query(col, where('status', '==', 'failed'))),
        getDocs(query(col)),
      ]);

      const byType: { [k: string]: number } = {};
      allDocsSnap.docs.forEach(d => {
        const t = d.data().reportType as string;
        if (t) byType[t] = (byType[t] || 0) + 1;
      });

      return {
        total:      totalSnap.data().count,
        completed:  completedSnap.data().count,
        generating: generatingSnap.data().count,
        failed:     failedSnap.data().count,
        byType,
      };
    } catch (error) {
      console.error('Error getting report stats:', error);
      throw error;
    }
  }
}
